import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  collection,
  endBefore,
  getDocs,
  limit,
  limitToLast,
  orderBy,
  query,
  startAfter,
  where,
} from "firebase/firestore";
import { CommonTable } from "../../../shared/ui/table/CommonTable";
import { db } from "../../../firebase";
import {
  ALL_TOWNS,
  TOWN_SEEC_ORGANIZATIONS,
} from "../../../shared/constants/townNames";
import { SEEC_CANDIDATE_COMMITTEE_NAMES } from "../../../shared/constants/seecCandidateCommittees";

import { SeecPageHeader } from "./SeecPageHeader";

const PAGE_SIZE = 250;
const PARTY_OPTIONS = [
  "All Parties",
  "Democrat",
  "Republican",
  "Green Party",
  "Independent Party",
  "Working Families",
  "United Community Party",
  "Forward Party",
  "Libertarian Party",
];
const YEARS_OPTIONS = ["All", "3", "5", "10", "15"];
const DONATION_TARGET_OPTIONS = Array.from(new Set([
  ...ALL_TOWNS,
  ...Object.values(TOWN_SEEC_ORGANIZATIONS).flat(),
  ...SEEC_CANDIDATE_COMMITTEE_NAMES,
])).sort((left, right) => left.localeCompare(right));

function formatRoundedMoney(value) {
  return `$${Math.round(Number(value || 0)).toLocaleString()}`;
}

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-US");
}

export function SeecTopDonorsPage() {
  const [rows, setRows] = useState([]);
  const [firstDoc, setFirstDoc] = useState(null);
  const [lastDoc, setLastDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pageIndex, setPageIndex] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [hasPrevious, setHasPrevious] = useState(false);
  const [error, setError] = useState("");
  const [donationTargetFilterInput, setDonationTargetFilterInput] = useState("");
  const [selectedDonationTargetFilters, setSelectedDonationTargetFilters] = useState([]);
  const [partyFilter, setPartyFilter] = useState("All Parties");
  const [yearsFilter, setYearsFilter] = useState("5");
  const latestRequestIdRef = useRef(0);

  const normalizedDonationTargetFilter = useMemo(() => {
    const trimmed = donationTargetFilterInput.trim().toLowerCase();
    if (!trimmed) return "";
    const exactMatch = DONATION_TARGET_OPTIONS.find(
      (value) => value.toLowerCase() === trimmed,
    );
    return exactMatch || "";
  }, [donationTargetFilterInput]);

  const matchingDonationTargetOptions = useMemo(() => {
    const trimmed = donationTargetFilterInput.trim().toLowerCase();
    if (!trimmed) return [];

    return DONATION_TARGET_OPTIONS
      .filter((value) => (
        value.toLowerCase().startsWith(trimmed) &&
        !selectedDonationTargetFilters.includes(value)
      ))
      .slice(0, 8);
  }, [donationTargetFilterInput, selectedDonationTargetFilters]);

  const selectedDonationTargetSet = useMemo(
    () => new Set(selectedDonationTargetFilters),
    [selectedDonationTargetFilters],
  );

  const selectedDonationTargetTownFilters = useMemo(
    () => selectedDonationTargetFilters.filter((value) => ALL_TOWNS.includes(value)),
    [selectedDonationTargetFilters],
  );

  const hasNonTownDonationTargetFilters = useMemo(
    () => selectedDonationTargetFilters.some((value) => !ALL_TOWNS.includes(value)),
    [selectedDonationTargetFilters],
  );

  const minimumLastDonationDate = useMemo(() => {
    if (yearsFilter === "All") return null;

    const years = Number(yearsFilter);
    if (!Number.isFinite(years) || years <= 0) return null;

    const cutoff = new Date();
    cutoff.setFullYear(cutoff.getFullYear() - years);
    return cutoff;
  }, [yearsFilter]);

  const addDonationTargetFilter = useCallback((value) => {
    if (!value) return;

    setSelectedDonationTargetFilters((current) => (
      current.includes(value) ? current : [...current, value]
    ));
    setDonationTargetFilterInput("");
  }, []);

  const removeDonationTargetFilter = useCallback((value) => {
    setSelectedDonationTargetFilters((current) => (
      current.filter((currentValue) => currentValue !== value)
    ));
  }, []);

  const loadPage = useCallback(async ({
    cursor = null,
    direction = "initial",
    nextPageIndex = 0,
  } = {}) => {
    const requestId = latestRequestIdRef.current + 1;
    latestRequestIdRef.current = requestId;
    setLoading(true);
    setError("");
    setRows([]);

    try {
      const hasDonationTargetFilters = selectedDonationTargetFilters.length > 0;
      const isFiltered = hasDonationTargetFilters || partyFilter !== "All Parties";
      const donorCollection = collection(db, "SEEC_DONORS");
      const constraints = [orderBy("donationSummary.totalAmount", "desc")];

      if (isFiltered) {
        if (hasDonationTargetFilters && partyFilter === "All Parties") {
          constraints.unshift(
            where(
              hasNonTownDonationTargetFilters
                ? "searchableDonationTargets"
                : "searchableTownNames",
              "array-contains-any",
              (
                hasNonTownDonationTargetFilters
                  ? selectedDonationTargetFilters
                  : selectedDonationTargetTownFilters
              ).slice(0, 10),
            ),
          );
        } else if (!hasDonationTargetFilters && partyFilter !== "All Parties") {
          constraints.unshift(
            where("searchablePartyAffiliations", "array-contains", partyFilter),
          );
        } else if (hasDonationTargetFilters && partyFilter !== "All Parties") {
          constraints.unshift(
            where(
              hasNonTownDonationTargetFilters
                ? "searchableDonationTargets"
                : "searchableTownNames",
              "array-contains-any",
              (
                hasNonTownDonationTargetFilters
                  ? selectedDonationTargetFilters
                  : selectedDonationTargetTownFilters
              ).slice(0, 10),
            ),
          );
        }
      }

      if (direction === "next" && cursor) {
        constraints.push(startAfter(cursor));
        constraints.push(limit(PAGE_SIZE));
      } else if (direction === "previous" && cursor) {
        constraints.push(endBefore(cursor));
        constraints.push(limitToLast(PAGE_SIZE));
      } else {
        constraints.push(limit(PAGE_SIZE));
      }

      const snap = await getDocs(query(donorCollection, ...constraints));
      if (latestRequestIdRef.current !== requestId) {
        return;
      }

      const rawRows = snap.docs.map((docSnap) => {
        const data = docSnap.data();
        const summary = data.donationSummary || {};
        const parties = data.searchablePartyAffiliations || [];
        const towns = data.searchableTownNames || [];
        const organizations = data.searchableOrganizationNames || [];
        const donationTargets = data.searchableDonationTargets || [
          ...towns,
          ...organizations,
        ];
        const lastDonationDate = summary.lastDonationDate
          ? new Date(summary.lastDonationDate)
          : null;

        if (
          selectedDonationTargetFilters.length > 0 &&
          !donationTargets.some((value) => selectedDonationTargetSet.has(value))
        ) {
          return null;
        }

        if (partyFilter !== "All Parties" && !parties.includes(partyFilter)) {
          return null;
        }

        if (
          minimumLastDonationDate &&
          (!lastDonationDate || Number.isNaN(lastDonationDate.getTime()) || lastDonationDate < minimumLastDonationDate)
        ) {
          return null;
        }

        return {
          id: docSnap.id,
          Name: data.displayName || "—",
          "Total Donated": summary.totalAmount || 0,
          "# of Donations": summary.donationCount || 0,
          "First Donation": summary.firstDonationDate || null,
          "Last Donation": summary.lastDonationDate || null,
        };
      }).filter(Boolean).slice(0, PAGE_SIZE);

      const rankOffset = nextPageIndex * PAGE_SIZE;
      const nextRows = rawRows.map((row, index) => ({
        ...row,
        Rank: rankOffset + index + 1,
      }));

      setRows(nextRows);
      setFirstDoc(snap.docs[0] || null);
      setLastDoc(snap.docs[snap.docs.length - 1] || null);
      setHasMore(snap.docs.length === PAGE_SIZE);
      setHasPrevious(nextPageIndex > 0);
      setPageIndex(nextPageIndex);
    } catch (loadPageError) {
      if (latestRequestIdRef.current !== requestId) {
        return;
      }
      setError(loadPageError.message || "Unable to load top donors.");
    } finally {
      if (latestRequestIdRef.current === requestId) {
        setLoading(false);
      }
    }
  }, [
    hasNonTownDonationTargetFilters,
    minimumLastDonationDate,
    partyFilter,
    selectedDonationTargetFilters,
    selectedDonationTargetSet,
    selectedDonationTargetTownFilters,
  ]);

  useEffect(() => {
    setFirstDoc(null);
    setLastDoc(null);
    setHasPrevious(false);
    setHasMore(true);
    setPageIndex(0);
    loadPage({ direction: "initial", nextPageIndex: 0 });
  }, [loadPage]);

  const columns = useMemo(
    () => [
      {
        key: "Rank",
        label: "Rank",
        style: { width: "80px", textAlign: "center" },
        align: "center",
        headerAlign: "center",
      },
      {
        key: "Name",
        label: "Donor Name",
        style: { width: "260px" },
        render: (row) => (
          <Link
            to={`/seec/donor/${row.id}`}
            className="text-blue-700 hover:text-blue-900 underline"
          >
            {row.Name}
          </Link>
        ),
      },
      {
        key: "Total Donated",
        label: "Total Donated",
        style: { width: "140px" },
        sortKey: "Total Donated",
        align: "right",
        headerAlign: "center",
        render: (row) => formatRoundedMoney(row["Total Donated"]),
      },
      {
        key: "# of Donations",
        label: "# of Donations",
        style: { width: "130px", textAlign: "center" },
        align: "center",
        headerAlign: "center",
      },
      {
        key: "First Donation",
        label: "First Donation",
        style: { width: "120px", textAlign: "center" },
        sortKey: "First Donation",
        align: "center",
        headerAlign: "center",
        render: (row) => formatDate(row["First Donation"]),
      },
      {
        key: "Last Donation",
        label: "Last Donation",
        style: { width: "120px", textAlign: "center" },
        sortKey: "Last Donation",
        align: "center",
        headerAlign: "center",
        render: (row) => formatDate(row["Last Donation"]),
      },
    ],
    [],
  );

  return (
    <div className="space-y-5">
      <SeecPageHeader title="Top donors" active="donors" description="Compare contributors, narrow by campaign or town, and open a donor for their contribution history." />
      <section className="min-w-0">
        <div className="bg-base-100 shadow-md rounded-lg p-2 border border-gray-300">
          <div className="bg-gray-100 px-3 py-2 font-semibold text-lg text-gray-900">
            Filter donors
          </div>

          <div className="bg-white p-4">
            <div className="mb-4 grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_220px_140px] gap-4">
              <label className="flex flex-col gap-1 text-sm text-gray-700">
                <span className="font-medium">Donation Targets</span>
                <input
                  type="text"
                  value={donationTargetFilterInput}
                  onChange={(event) => {
                    const nextValue = event.target.value;
                    setDonationTargetFilterInput(nextValue);

                    const trimmed = nextValue.trim().toLowerCase();
                    const exactMatch = DONATION_TARGET_OPTIONS.find(
                      (value) => value.toLowerCase() === trimmed,
                    );

                    if (exactMatch) {
                      addDonationTargetFilter(exactMatch);
                    }
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" && normalizedDonationTargetFilter) {
                      event.preventDefault();
                      addDonationTargetFilter(normalizedDonationTargetFilter);
                    }
                  }}
                  placeholder="Start typing a town, political committee, or candidate"
                  className="border border-gray-300 rounded-md px-3 py-2"
                />
                {matchingDonationTargetOptions.length > 0 ? (
                  <div className="border border-gray-200 rounded-md bg-white shadow-sm overflow-hidden">
                    {matchingDonationTargetOptions.map((value) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => addDonationTargetFilter(value)}
                        className="block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                      >
                        {value}
                      </button>
                    ))}
                  </div>
                ) : null}
                {selectedDonationTargetFilters.length > 0 ? (
                  <div className="flex flex-wrap gap-2 pt-2">
                    {selectedDonationTargetFilters.map((value) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => removeDonationTargetFilter(value)}
                        aria-label={`Remove ${value} filter`}
                        className="inline-flex items-center gap-2 rounded-full border border-blue-300 bg-blue-50 px-3 py-1 text-xs text-blue-800"
                      >
                        <span>{value}</span>
                        <span aria-hidden="true" className="font-semibold leading-none">×</span>
                      </button>
                    ))}
                  </div>
                ) : null}
              </label>

              <label className="flex flex-col gap-1 text-sm text-gray-700">
                <span className="font-medium">Party</span>
                <select
                  value={partyFilter}
                  onChange={(event) => setPartyFilter(event.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-2 bg-white"
                >
                  {PARTY_OPTIONS.map((party) => (
                    <option key={party} value={party}>
                      {party}
                    </option>
                  ))}
                </select>
              </label>

              <label className="flex flex-col gap-1 text-sm text-gray-700">
                <span className="font-medium">Last donation</span>
                <select
                  value={yearsFilter}
                  onChange={(event) => setYearsFilter(event.target.value)}
                  className="border border-gray-300 rounded-md px-3 py-2 bg-white"
                >
                  {YEARS_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option === "All" ? "Any time" : `Within ${option} years`}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {donationTargetFilterInput && !normalizedDonationTargetFilter ? (
              <div className="mb-4 text-sm text-amber-700">
                Choose a town, political committee, or candidate from the autocomplete list.
              </div>
            ) : null}

            {loading ? (
              <div className="text-sm text-gray-600">Loading top donors...</div>
            ) : error ? (
              <div className="text-sm text-red-600">{error}</div>
            ) : (
              <>
                <CommonTable
                  title=""
                  columns={columns}
                  data={rows}
                  headerColor="bg-blue-50"
                  showAggregateTotal={false}
                  defaultSortField="Total Donated"
                  defaultSortOrder="desc"
                  emptyMessage="No donor data found."
                />

                <div className="mt-4 flex items-center justify-between gap-4 flex-wrap">
                  <div className="text-sm text-gray-600">
                    Loaded {rows.length.toLocaleString()} donors on page {(pageIndex + 1).toLocaleString()}.
                  </div>

                  <div className="flex items-center gap-2 ml-auto">
                    <button
                      type="button"
                      onClick={() =>
                        loadPage({
                          direction: "previous",
                          cursor: firstDoc,
                          nextPageIndex: Math.max(pageIndex - 1, 0),
                        })
                      }
                      disabled={loading || !hasPrevious || !firstDoc}
                      className={`px-3 py-1.5 rounded border text-sm ${
                        loading || !hasPrevious || !firstDoc
                          ? "border-gray-300 bg-gray-100 text-gray-500 cursor-not-allowed"
                          : "border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100"
                      }`}
                    >
                      Previous 250
                    </button>

                    <button
                      type="button"
                      onClick={() =>
                        loadPage({
                          direction: "next",
                          cursor: lastDoc,
                          nextPageIndex: pageIndex + 1,
                        })
                      }
                      disabled={loading || !hasMore || !lastDoc}
                      className={`px-3 py-1.5 rounded border text-sm ${
                        loading || !hasMore || !lastDoc
                          ? "border-gray-300 bg-gray-100 text-gray-500 cursor-not-allowed"
                          : "border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100"
                      }`}
                    >
                      Next 250
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

export default SeecTopDonorsPage;
